import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Modal, Form, Input, message, Switch, Pagination } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, LinkOutlined } from '@ant-design/icons';
import axios from 'axios';
import { SidebarLayout } from '../components/SidebarLayout';
import UserClientsModal from './UserClients';

const api = `${process.env.REACT_APP_API_URL}/users`;

const UserForm = ({ visible, onCancel, onSubmit, initialValues }) => {
  const [form] = Form.useForm();
  useEffect(() => {
    if (visible) form.setFieldsValue(initialValues || {}); else form.resetFields();
  }, [visible, initialValues]);
  return (
    <Modal
      visible={visible}
      title={initialValues ? 'Editar Usuário' : 'Novo Usuário'}
      onCancel={onCancel}
      onOk={() => {
        form.validateFields()
          .then(values => {
            form.resetFields();
            onSubmit(values);
          });
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="dsname"
          label="Nome"
          rules={[
            { required: true, message: 'Nome é obrigatório' },
            { max: 255, message: 'Máximo de 255 caracteres' }
          ]}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="dsemail"
          label="Email"
          rules={[
            { required: true, message: 'Email é obrigatório' },
            { type: 'email', message: 'Email inválido' },
            { max: 255, message: 'Máximo de 255 caracteres' }
          ]}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="dspassword"
          label="Senha"
          rules={[]}
        >
          <Input.Password maxLength={255} />
        </Form.Item>
        <Form.Item name="nrisadmin" label="Administrador" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [clientsModalVisible, setClientsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(api);
      setUsers(res.data);
    } catch {
      messageApi.error('Erro ao buscar usuários');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async values => {
    try {
      await axios.post(api, values);
      messageApi.success('Usuário criado com sucesso');
      fetchUsers();
    } catch (error) {
      var apiMsg = 'Erro ao criar usuário';
      if (error.response?.status != 500) {
        apiMsg +=  ': ' + error.response?.data?.error || '!';
      }
      messageApi.error(apiMsg);
    }
    setFormVisible(false);
  };

  const handleUpdate = async values => {
    try {
      await axios.put(`${api}/${editingUser.id}`, values);
      messageApi.success('Usuário atualizado com sucesso');
      fetchUsers();
    } catch (error) {
      var apiMsg = 'Erro ao atualizar usuário';
      if (error.response?.status != 500) {
        apiMsg +=  ': ' + error.response?.data?.error || '!';
      }
      messageApi.error(apiMsg);
    }
    setEditingUser(null);
    setFormVisible(false);
  };

  const handleDelete = id => {
    Modal.confirm({
      title: 'Tem certeza que deseja remover este usuário?',
      content: 'Esta ação não poderá ser desfeita.',
      okText: 'Sim, remover',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await axios.delete(`${api}/${id}`);
          messageApi.success('Usuário removido com sucesso');
          fetchUsers();
        } catch (error) {
          var apiMsg = 'Erro ao remover usuário';
          if (error.response?.status != 500) {
            apiMsg +=  ': ' + error.response?.data?.error || '!';
          }
          messageApi.error(apiMsg);
        }
      }
    });
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      let user = await axios.put(`${api}/${id}/toggle-status`);
      console.log(user);
      messageApi.success(`Usuário ${currentStatus ? 'desativado' : 'ativado'} com sucesso`);
      fetchUsers();
    } catch (error) {
      var apiMsg = 'Erro ao alterar status do usuário';
      if (error.response?.status != 500) {
        apiMsg +=  ': ' + error.response?.data?.error || '!';
      }
      messageApi.error(apiMsg);
    }
  };

  const handleOpenClientsModal = (user) => {
    setSelectedUser(user);
    setClientsModalVisible(true);
  };

  const filteredUsers = users.filter(user =>
    user.dsname.toLowerCase().includes(searchText.toLowerCase()) ||
    user.dsemail.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <SidebarLayout>
      {contextHolder}
      <div style={{ padding: 24 }}>
        <h1><UserOutlined style={{ fontSize: 24 }} /> Usuários</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingUser(null);
              setFormVisible(true);
            }}
          >
            Novo
          </Button>
          <Input.Search
            placeholder="Buscar usuário"
            allowClear
            onSearch={value => setSearchText(value)}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        <Row gutter={[42, 32]}>
          {filteredUsers
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map(user => (
              <Col xs={24} sm={12} md={8} lg={8} key={user.id}>
                <Card
                  actions={[
                    <EditOutlined key="edit" onClick={() => { setEditingUser(user); setFormVisible(true); }} />,
                    <LinkOutlined key="link" onClick={() => handleOpenClientsModal(user)} />,
                    <DeleteOutlined key="delete" style={{ color: 'red' }} onClick={() => handleDelete(user.id)} />
                  ]}
                  style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', padding: 0 }}
                >
                  <p><b>Nome:</b> {user.dsname}</p>
                  <p><b>Email:</b> {user.dsemail}</p>
                  <p><b>Administrador:</b> {user.nrisadmin ? 'Sim' : 'Não'}</p>
                  <p>
                    <b>Status:</b>{' '}
                    <Switch
                      checked={user.nrisactive}
                      onChange={() => handleToggleStatus(user.id, user.nrisactive)}
                      size="small"
                    />
                  </p>
                </Card>
              </Col>
            ))}
        </Row>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'end' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredUsers.length}
            onChange={page => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>

        <UserForm
          visible={formVisible}
          onCancel={() => setFormVisible(false)}
          onSubmit={editingUser ? handleUpdate : handleCreate}
          initialValues={editingUser}
        />

        <UserClientsModal
          visible={clientsModalVisible}
          onCancel={() => setClientsModalVisible(false)}
          userId={selectedUser?.id}
          onSave={() => {
            setClientsModalVisible(false);
            fetchUsers();
          }}
        />
      </div>
    </SidebarLayout>
  );
};